import './ReceptionShow.scss';

import { DateField, Show, TextField } from '@refinedev/antd';
import { useList, useOne, useShow } from '@refinedev/core';
import { Alert, Card, Divider, Tag } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LinkTextField from '@/components/general/LinkTextField';
import PackageItemsList from '@/components/logistic/PackageItems/PackageItemsList';
import PackageStateTag from '@/components/logistic/PackageStateTag';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { useUserContext } from '@/contexts/UserContext';
import CompanyContact from '@/model/General/CompanyContact';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { PRODUCT_TYPES } from '@/model/Logistic/PackageItem/ProductTypes';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Reception } from '@/model/Logistic/Reception/Reception';
import { RECEPTION_TYPE } from '@/model/Logistic/Reception/ReceptionType';
import { Ticket } from '@/model/Ticket/Ticket';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { RESOURCES } from '@/Resources';

import ReceptionShowContactInfo from './ReceptionShow/ReceptionShowContactInfo';
import ReceptionShowDetail from './ReceptionShow/ReceptionShowDetail';
import ReceptionShowFiles from './ReceptionShow/ReceptionShowFiles';
import ReceptionShowHeaderButtons from './ReceptionShow/ReceptionShowHeaderButtons';
import ReceptionShowLogistic from './ReceptionShow/ReceptionShowLogistic';
import ReceptionShowReturnAddress from './ReceptionShow/ReceptionShowReturnAddress';
import ReceptionShowTechnician from './ReceptionShow/ReceptionShowTechnician';

export const ReceptionShow = () => {
  const { t } = useTranslation();
  const { userWorkArea } = useUserContext();
  const { hasPermission } = usePermissionContext();
  const { packageItemRepository } = useRepositoriesContext();
  const [packageItems, setPackageItems] = useState<PackageItem[]>([]);
  const [blockChangeState, setBlockChangeState] = useState<{ id: string; message: string }[]>([]);
  const {
    result: record,
    query: { isLoading, refetch },
  } = useShow<Reception>();
  const {
    result: ticketResult,
    query: { isLoading: ticketIsLoading },
  } = useOne<Ticket>({
    resource: RESOURCES.TICKETS,
    id: record?.ticket || '',
    queryOptions: {
      enabled: !!record && !!record.ticket,
    },
  });
  const {
    result: createdByResult,
    query: { isLoading: createdByIsLoading },
  } = useOne({
    resource: 'users',
    id: record?.created_by || '',
    queryOptions: {
      enabled: !!record && !!record.created_by,
      retry: false,
    },
  });
  const {
    result: companyContactResult,
    query: { isLoading: companyContactIsLoading },
  } = useOne<CompanyContact>({
    resource: 'company_contact',
    id: record?.company_contact || '',
    queryOptions: {
      enabled: !!record && !!record.company_contact,
      retry: false,
    },
  });
  const loading = useMemo(
    () => isLoading || ticketIsLoading || createdByIsLoading || companyContactIsLoading,
    [isLoading, ticketIsLoading, createdByIsLoading, companyContactIsLoading],
  );
  const itemsEditMode = useMemo(
    () =>
      hasPermission('edit')
        ? record?.status == PACKAGE_STATES.FINALIZADO || record?.status == PACKAGE_STATES.CANCELADO
          ? 'disabled'
          : record?.status == PACKAGE_STATES.PENDIENTE
            ? record?.tipo == RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE
              ? 'partial'
              : 'enabled'
            : userWorkArea == USER_WORK_AREA.LOGISTICS
              ? record?.tipo == RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE
                ? 'partial'
                : 'partial-with-news'
              : 'disabled'
        : 'disabled',
    [hasPermission, record, userWorkArea],
  );
  const canEditResolution = useMemo(() => {
    return userWorkArea === USER_WORK_AREA.LOGISTICS && record?.status === PACKAGE_STATES.RECIBIDO;
  }, [userWorkArea, record?.status]);
  const canFinishReception = useMemo(() => {
    return (
      userWorkArea === USER_WORK_AREA.LOGISTICS &&
      record?.status === PACKAGE_STATES.RECIBIDO &&
      packageItems.every(
        pi =>
          pi.item_received_state != undefined &&
          (pi.product_type == PRODUCT_TYPES.UNIT
            ? pi.execute_amount == 0 || pi.unidad != undefined
            : pi.product_type == PRODUCT_TYPES.DEVICE
              ? pi.execute_amount == 0 || pi.device != undefined
              : true),
      )
    );
  }, [userWorkArea, record?.status, packageItems]);
  const canMarkAsSendingReception = useMemo(
    () =>
      record?.tipo != RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE ||
      (record?.status === PACKAGE_STATES.PENDIENTE &&
        packageItems.every(
          pi =>
            pi.requested_amount ==
            (pi.discarded_amount ?? 0) +
              (pi.technician_stock_amount ?? 0) +
              (pi.to_return_amount ?? 0),
        )),
    [record?.tipo, record?.status, packageItems],
  );
  const refetchResource = useCallback(async () => {
    void refetch().then(() => {
      if (record?.id)
        void packageItemRepository
          .getPackageItemsFromPackages([record.id])
          .then(pi => setPackageItems(pi));
    });
  }, [packageItemRepository, refetch, record?.id]);
  // Referencia para poder visualizar las OS relacionadas a recepciones que fueron previamente creadas en un envío sin técnico
  const {
    result: { data: serviceOrders },
  } = useList({
    resource: RESOURCES.SERVICE_ORDERS,
    filters: [
      {
        field: 'tickets',
        operator: 'eq',
        value: record?.ticket,
      },
    ],
    queryOptions: {
      enabled: !!record?.ticket,
    },
  });

  return (
    <Show
      isLoading={loading}
      headerButtons={buttonProps =>
        record && (
          <ReceptionShowHeaderButtons
            record={record}
            refineButtonsProps={buttonProps}
            refetch={() => void refetchResource()}
            blockChangeState={blockChangeState}
            canFinishReception={canFinishReception}
            canMarkAsSendingReception={canMarkAsSendingReception}
          />
        )
      }>
      <div className="ReceptionShow">
        <div className="ReceptionShowDataRow">
          <div className="ReceptionShowData ReceptionShowDataId">
            <b>
              {t('Id de recepción')}: {record?.id}
            </b>
            {record?.status != undefined && (
              <div className="TicketShowRowBooleans">
                <PackageStateTag packageStatus={record?.status} />
                {record?.tipo == RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE && (
                  <Tag color="geekblue">{t('Automática')}</Tag>
                )}
              </div>
            )}
          </div>

          <div className="ReceptionShowData">
            <div>
              <b>{t('Fecha de creación')}: </b>
              <DateField value={record?.created_at} />
            </div>
            <div>
              <b>{t('Fecha última modificación')}: </b>
              <DateField value={record?.updated_at} />
            </div>

            <div>
              <b>{t('Creado por')}: </b>
              <TextField
                value={
                  createdByResult
                    ? `${createdByResult?.first_name} ${createdByResult?.last_name} (${createdByResult?.id})`
                    : ''
                }
              />
            </div>
          </div>
        </div>
        {serviceOrders && serviceOrders.length > 0 && (
          <Alert
            style={{ marginTop: 12 }}
            type="warning"
            showIcon
            message={
              <>
                {t('Existen órdenes de servicio asociadas a esta recepción')}:{' '}
                {serviceOrders.map((so, index) => (
                  <span key={so.id}>
                    {index > 0 && ', '}
                    <LinkTextField
                      label={`${t('Orden de servicio')} #${so.id}`}
                      to={`/${RESOURCES.SERVICE_ORDERS}/show/${so.id}`}
                      external={true}
                    />
                  </span>
                ))}
              </>
            }
          />
        )}
        {record?.status == PACKAGE_STATES.CANCELADO && record?.cancellation_motive && (
          <Card className="ReceptionShowSection">
            <div className="ReceptionShowData">
              <b>{t('Motivo de cancelación')}</b>
              <TextField value={record?.cancellation_motive} />
            </div>
          </Card>
        )}
        <Card className="ReceptionShowSection">
          <div className="ReceptionShowTitle">{t('Detalles de la recepción')}</div>
          <ReceptionShowDetail
            record={record}
            ticket={ticketResult}
            refetch={() => void refetch()}
          />
        </Card>
        {record?.tipo != RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE && (
          <>
            <Card className="ReceptionShowSection">
              <ReceptionShowContactInfo companyContact={companyContactResult} />
            </Card>
            <Card className="ReceptionShowSection">
              <div className="ReceptionShowTitle">{t('Dirección de devolución')}</div>
              <ReceptionShowReturnAddress record={record} />
            </Card>
          </>
        )}
        <Card className="ReceptionShowSection">
          <div className="ReceptionShowTitle">{t('Logística')}</div>
          <ReceptionShowLogistic record={record} refetch={() => void refetch()} />
        </Card>

        {record?.tipo == RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE && (
          <Card className="ReceptionShowSection">
            <ReceptionShowTechnician record={record} refetch={() => void refetchResource()} />
          </Card>
        )}
        <div className="ReceptionShowTitle">{t('Elementos')}</div>
        <PackageItemsList
          packageItems={packageItems}
          setPackageItems={setPackageItems}
          editMode={itemsEditMode}
          package={{ id: record?.id }}
          setBlockChangeState={setBlockChangeState}
          canEditResolution={canEditResolution}
          refetchResource={refetchResource}
        />
        {record?.tipo == RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE && (
          <Alert
            style={{ marginTop: 16 }}
            showIcon
            type="info"
            message={t(
              'No se pueden modificar los elementos de una recepción creada automáticamente',
            )}
          />
        )}

        {(record?.status == PACKAGE_STATES.RECIBIDO ||
          record?.status == PACKAGE_STATES.FINALIZADO) && (
          <>
            <Divider />
            <div className="ReceptionShowTitle">{t('Fotos')}</div>
            <ReceptionShowFiles record={record} />
          </>
        )}
      </div>
    </Show>
  );
};
